import { OP_AREA_ALPHA_RING } from '../data/realisticExerciseScenarioData';

const EARTH_M_PER_DEG_LAT = 111320;
const EARTH_RADIUS_M = 6371000;

/** Great-circle distance between two WGS84 points (meters). */
export function haversineDistanceMeters(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Ray-cast point-in-polygon. Ring is [lon, lat][] closed or unclosed.
 */
function pointInPolygonLonLat(lon, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const denom = yj - yi;
    if (Math.abs(denom) < 1e-15) continue;
    const intersect = (yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / denom + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Shortest distance from point to segment AB in meters (local equirectangular, fine for small regions).
 */
function distPointToSegmentMeters(lon, lat, ax, ay, bx, by) {
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const mPerLon = EARTH_M_PER_DEG_LAT * cosLat;
  const px = lon * mPerLon;
  const py = lat * EARTH_M_PER_DEG_LAT;
  const axm = ax * mPerLon;
  const aym = ay * EARTH_M_PER_DEG_LAT;
  const bxm = bx * mPerLon;
  const bym = by * EARTH_M_PER_DEG_LAT;
  const abx = bxm - axm;
  const aby = bym - aym;
  const apx = px - axm;
  const apy = py - aym;
  const ab2 = abx * abx + aby * aby;
  let t = ab2 < 1e-12 ? 0 : (apx * abx + apy * aby) / ab2;
  t = Math.max(0, Math.min(1, t));
  const qx = axm + t * abx;
  const qy = aym + t * aby;
  return Math.hypot(px - qx, py - qy);
}

/**
 * Distance from (lon, lat) to Op Alpha polygon boundary in meters. 0 if inside.
 */
export function distanceMetersToOpAlphaBoundary(lon, lat) {
  const ring = OP_AREA_ALPHA_RING;
  if (!ring || ring.length < 3) return Infinity;
  if (pointInPolygonLonLat(lon, lat, ring)) return 0;
  let min = Infinity;
  const n = ring.length;
  const closedDup =
    n > 2 &&
    ring[0][0] === ring[n - 1][0] &&
    ring[0][1] === ring[n - 1][1];
  const lastI = closedDup ? n - 2 : n - 1;
  for (let i = 0; i < lastI; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    const d = distPointToSegmentMeters(lon, lat, x1, y1, x2, y2);
    if (d < min) min = d;
  }
  return min;
}

/**
 * True if the point is inside Op Alpha or within bufferMeters of its edge.
 */
export function isVesselNearOpAlphaThreatZone(lon, lat, bufferMeters) {
  return distanceMetersToOpAlphaBoundary(lon, lat) <= bufferMeters;
}
